import { createContext, useContext, useState } from 'react';
import { DialogFilePreview } from 'components/shared/dialog/DialogFilePreview';
import { fileTypeChecks } from 'utils/file-icon';

export const FilePreviewDialogContext = createContext(null);

export const useFilePreviewDialog = () => {
  const context = useContext(FilePreviewDialogContext);
  if (!context) {
    throw new Error('useFilePreviewDialog must be used within FilePreviewDialogProvider');
  }
  return context;
};

export const FilePreviewDialogProvider = ({ children, ...props }) => {
  const [dialog, setDialog] = useState({
    show: false,
    file: null,
    files: [],
    fetchMoreFiles: null,
    attachmentPreview: null,
  });

  const closePreviewDialog = () => {
    setDialog({
      show: false,
      file: null,
      files: [],
      fetchMoreFiles: null,
      attachmentPreview: null,
    });
  };

  const openPreviewDialog = (file, files = [], options = {}) => {
    setDialog({
      show: true,
      file,
      files: files.filter((f) => !fileTypeChecks.isFileFolder(f)),
      fetchMoreFiles: options.fetchMoreFiles || null,
      attachmentPreview: options.attachmentPreview || null,
    });
  };

  const showNextFile = () => {
    const index = dialog.files.findIndex((f) => f.id === dialog.file?.id);

    if (index !== -1 && index < dialog.files.length - 1) {
      setDialog((prev) => ({ ...prev, file: dialog.files[index + 1] }));

      if (index + 1 === dialog.files.length - 1 && typeof dialog.fetchMoreFiles === 'function') {
        dialog.fetchMoreFiles();
      }
    }
  };

  const showPrevFile = () => {
    const index = dialog.files.findIndex((f) => f.id === dialog.file?.id);
    if (index > 0) {
      setDialog((prev) => ({ ...prev, file: dialog.files[index - 1] }));
    }
  };

  const setFileBlobUrl = (id, blobUrl) => {
    const updatedFiles = dialog?.files?.map((f) => (f.id === id ? { ...f, blobUrl } : f));

    setDialog((prev) => ({
      ...prev,
      files: updatedFiles,
      file: dialog.file?.id === id ? { ...prev.file, blobUrl } : { ...prev.file },
    }));
  };

  const setFilePreview = (id, preview) => {
    const updatedFiles = dialog?.files?.map((f) => (f.id === id ? { ...f, preview } : f));

    setDialog((prev) => ({
      ...prev,
      files: updatedFiles,
      file: dialog.file?.id === id ? { ...prev.file, preview } : { ...prev.file },
    }));
  };

  const setDialogFiles = (updateFiles) => {
    if (updateFiles?.length > dialog.files?.length) {
      setDialog((prev) => ({
        ...prev,
        files: updateFiles.filter((f) => !fileTypeChecks.isFileFolder(f)),
      }));
    }
  };

  const index = dialog?.files?.findIndex((f) => f.id === dialog.file?.id);
  const isFirst = index === 0;
  const isLast = dialog?.files && index === dialog.files.length - 1;

  return (
    <FilePreviewDialogContext.Provider
      value={{
        dialog,
        openPreviewDialog,
        closePreviewDialog,
        showNextFile,
        showPrevFile,
        setFileBlobUrl,
        setFilePreview,
        setDialogFiles,
      }}
      {...props}
    >
      {children}
      <DialogFilePreview
        attachmentPreview={dialog.attachmentPreview}
        show={dialog.show}
        showNext={isLast ? null : showNextFile}
        showPrev={isFirst ? null : showPrevFile}
        file={dialog.file}
        close={closePreviewDialog}
      />
    </FilePreviewDialogContext.Provider>
  );
};
